/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Typography2 } from '@coursera/cds-core';
import { CloseIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { WES_COACH_MODE_ID } from 'bundles/wes-common/components/WESAskCoursera/constants';

import _t from 'i18n!nls/wes-common';

import SparkleIcon from '../../../unified-common/components/coach-on-discovery/SparkleIcon';

type Position = 'bottom-right' | 'bottom-left';
type ColorVariant = 'default' | 'inverted';

type WESAskCourseraLauncherProps = {
  isOpen: boolean;
  onClick: () => void;
  position?: Position;
  buttonText?: string;
  colorVariant?: ColorVariant;
};

// Animation keyframes
const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
`;

const styles = {
  container: (position: Position) => css`
    position: fixed;
    ${position === 'bottom-right' ? 'right: var(--cds-spacing-800);' : 'left: var(--cds-spacing-400);'}
    bottom: var(--cds-spacing-600);
    z-index: 100;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--cds-spacing-200);
    pointer-events: none;
  `,

  // Wrapper for pill and close button with fade-out animation
  pillWrapper: (isDismissing: boolean) => css`
    display: flex;
    align-items: center;
    position: absolute;
    bottom: 55px;
    right: -10px;
    animation: ${isDismissing ? `${fadeOut} 0.3s ease-in-out forwards` : 'none'};
    transform-origin: bottom;
    pointer-events: auto;
  `,

  // Pill button that floats above the circular button
  pillButton: (colorVariant: ColorVariant) => css`
    display: flex;
    align-items: center;
    padding: var(--cds-spacing-50) var(--cds-spacing-150);
    background: ${colorVariant === 'inverted'
      ? 'var(--cds-color-emphasis-primary-background-strong, #0056d2)'
      : 'var(--cds-color-neutral-background-primary)'};
    border: none;
    border-radius: 28px;
    box-shadow: var(--cds-elevation-level1);
    cursor: pointer;
    white-space: nowrap;
    height: 32px;
    pointer-events: auto;
    transition: all 0.2s ease;

    &:active {
      transform: scale(0.98);
    }

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }
  `,

  // Close button for dismissing the pill
  closeButtonWrapper: (colorVariant: ColorVariant) => css`
    display: flex;
    width: 24px;
    height: 24px;
    padding: 6px;
    justify-content: center;
    align-items: center;
    border-radius: 12px;
    background: ${colorVariant === 'inverted'
      ? 'var(--cds-color-emphasis-primary-background-strong, #0056d2)'
      : 'var(--cds-color-neutral-background-primary)'};
    margin-left: var(--cds-spacing-100);
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: var(--cds-elevation-level1);

    &:hover {
      filter: brightness(0.95);
    }

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }

    svg {
      width: 12px;
      height: 12px;
      color: ${colorVariant === 'inverted'
        ? 'var(--cds-color-white-0)'
        : 'var(--cds-color-neutral-foreground-primary)'};
    }
  `,

  // Main launcher button (circular with sparkle icon)
  launcherButton: (colorVariant: ColorVariant) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    padding: 10px;
    border-radius: 24px;
    background-color: ${colorVariant === 'inverted'
      ? 'var(--cds-color-emphasis-primary-background-strong, #0056d2)'
      : 'var(--cds-color-white-0)'};
    border: ${colorVariant === 'inverted' ? 'none' : '1px solid var(--cds-color-neutral-stroke-primary)'};
    box-shadow: var(--cds-elevation-level1);
    cursor: pointer;
    pointer-events: auto;
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.05);
      box-shadow: var(--cds-elevation-level2);
      filter: ${colorVariant === 'inverted' ? 'brightness(0.95)' : 'none'};
    }

    &:focus-visible {
      outline: 3px solid var(--cds-color-white-0);
      outline-offset: 2px;
      border: 3px solid var(--cds-color-white-0);
      background-color: var(--cds-color-interactive-primary);
      box-shadow: 0 0 0 6px var(--cds-color-interactive-primary), var(--cds-elevation-level2);
    }

    &:active {
      transform: scale(0.98);
      filter: ${colorVariant === 'inverted' ? 'brightness(0.9)' : 'none'};
    }

    path {
      fill: ${colorVariant === 'inverted' ? 'var(--cds-color-white-0)' : 'var(--cds-color-interactive-primary)'};
    }
  `,
};

const WESAskCourseraLauncher = ({
  isOpen,
  onClick,
  position = 'bottom-right',
  buttonText = _t('Ask Coursera'),
  colorVariant = 'default',
}: WESAskCourseraLauncherProps) => {
  const [isPillDismissed, setIsPillDismissed] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const track = useTracker();
  const handleClose = useCallback<(e: React.MouseEvent) => void>((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDismissing(true);
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setIsPillDismissed(true);
    }, 300);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setIsPillDismissed(true);
      setIsDismissing(true);
    }
  }, [isOpen]);

  const handlePillClick = () => {
    onClick();

    track('interact_coach', {
      coachInteractionType: 'launch_coach_tool',
      coachAction: 'Ask Coursera Pill',
      coach: {
        mode: WES_COACH_MODE_ID,
      },
    });
  };

  return (
    <div css={styles.container(position)} data-testid="WESAskCourseraLauncher">
      {/* Pill prompt - visible when chat is closed and not dismissed */}
      {!isPillDismissed && (
        <div css={styles.pillWrapper(isDismissing)}>
          <div
            role="button"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }}
            tabIndex={0}
            onClick={handlePillClick}
            css={styles.pillButton(colorVariant)}
            data-testid="WESAskCourseraLauncher-pill"
            aria-label={_t('Open AI Chatbot')}
          >
            <Typography2
              variant="bodyPrimary"
              color={colorVariant === 'inverted' ? 'invertBody' : 'body'}
              component="p"
            >
              {buttonText}
            </Typography2>
          </div>

          <div
            css={styles.closeButtonWrapper(colorVariant)}
            role="button"
            onClick={handleClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClose(e as unknown as React.MouseEvent);
              }
            }}
            tabIndex={0}
            aria-label={_t('Close')}
            data-testid="WESAskCourseraLauncher-close"
          >
            <CloseIcon size="small" />
          </div>
        </div>
      )}

      <button
        type="button"
        css={styles.launcherButton(colorVariant)}
        onClick={onClick}
        aria-label={isOpen ? _t('Close AI Chatbot') : _t('Open AI Chatbot')}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-controls="wes-chatbot-dialog"
        data-testid="WESAskCourseraLauncher-circular"
      >
        <SparkleIcon size="large" />
      </button>
    </div>
  );
};

export default WESAskCourseraLauncher;
